import * as React from 'react';
import { Blue, Neutral, Yellow } from 'glints-aries/lib/@next/utilities/colors';

const N_LINE = Neutral.B85;
const N_MUTED = Neutral.B40;
const WHITE = Neutral.B100;

interface ArtProps {
  className?: string;
}

export const TalentArt: React.FC<ArtProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 320 176"
    role="presentation"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <clipPath id="glints-talent-avatar">
        <circle cx="54" cy="76" r="19" />
      </clipPath>
    </defs>

    <rect
      x="52"
      y="12"
      width="216"
      height="26"
      rx="8"
      fill={WHITE}
      stroke={N_LINE}
      strokeWidth="1.5"
    />
    <rect
      x="38"
      y="24"
      width="244"
      height="26"
      rx="8"
      fill={WHITE}
      stroke={N_LINE}
      strokeWidth="1.5"
    />

    <rect
      x="22"
      y="40"
      width="276"
      height="122"
      rx="12"
      fill={WHITE}
      stroke={N_LINE}
      strokeWidth="1.5"
    />

    <circle cx="54" cy="76" r="19" fill={Blue.S08} />
    <g clipPath="url(#glints-talent-avatar)">
      <circle cx="54" cy="70" r="7" fill={Blue.Brand} />
      <ellipse cx="54" cy="92" rx="12" ry="9" fill={Blue.Brand} />
    </g>

    <rect x="84" y="66" width="104" height="9" rx="4.5" fill={N_MUTED} />
    <rect x="84" y="82" width="68" height="7" rx="3.5" fill={N_LINE} />

    <rect x="38" y="112" width="66" height="22" rx="11" fill={Blue.S08} />
    <rect x="110" y="112" width="52" height="22" rx="11" fill={Blue.S08} />
    <rect x="168" y="112" width="74" height="22" rx="11" fill={Blue.S08} />

    <g>
      <rect x="230" y="62" width="52" height="26" rx="13" fill={Yellow.Brand} />
      <path
        d="M244 75.5l4.6 4.6 9-9"
        fill="none"
        stroke={Neutral.B18}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="270" cy="75" r="3" fill={Neutral.B18} />
    </g>

    <path
      d="M286 44l2.4 6.1 6.1 2.4-6.1 2.4-2.4 6.1-2.4-6.1-6.1-2.4 6.1-2.4z"
      fill={Yellow.Brand}
    />
  </svg>
);

export const EmployerArt: React.FC<ArtProps> = ({ className }) => {
  const rows = [
    { y: 84, bar: 92, sub: 58, top: true },
    { y: 110, bar: 76, sub: 46, top: false },
    { y: 136, bar: 84, sub: 52, top: false },
  ];

  return (
    <svg
      className={className}
      viewBox="0 0 320 176"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="22"
        y="14"
        width="276"
        height="148"
        rx="12"
        fill={WHITE}
        stroke={N_LINE}
        strokeWidth="1.5"
      />

      <rect x="38" y="30" width="30" height="30" rx="8" fill={Yellow.Brand} />
      <path
        d="M46 52v-9m7 9v-14m7 14v-6"
        stroke={Neutral.B18}
        strokeWidth="2.6"
        strokeLinecap="round"
      />

      <rect x="78" y="34" width="112" height="9" rx="4.5" fill={N_MUTED} />
      <rect x="78" y="49" width="74" height="7" rx="3.5" fill={N_LINE} />

      <rect x="234" y="32" width="48" height="22" rx="11" fill={Blue.S08} />
      <circle cx="246" cy="43" r="4" fill={Blue.Brand} />
      <rect x="255" y="39" width="19" height="8" rx="4" fill={Blue.Brand} />

      <line
        x1="38"
        y1="72"
        x2="282"
        y2="72"
        stroke={N_LINE}
        strokeWidth="1.5"
      />

      {rows.map((row) => (
        <g key={row.y}>
          <circle
            cx="49"
            cy={row.y + 9}
            r="11"
            fill={row.top ? Yellow.S45 : Blue.S08}
          />
          <circle
            cx="49"
            cy={row.y + 5}
            r="4"
            fill={row.top ? Yellow.S87 : Blue.Brand}
          />
          <ellipse
            cx="49"
            cy={row.y + 18}
            rx="7.5"
            ry="6"
            fill={row.top ? Yellow.S87 : Blue.Brand}
          />
          <rect
            x="68"
            y={row.y + 2}
            width={row.bar}
            height="8"
            rx="4"
            fill={N_MUTED}
          />
          <rect
            x="68"
            y={row.y + 15}
            width={row.sub}
            height="6"
            rx="3"
            fill={N_LINE}
          />
          <rect
            x="236"
            y={row.y + 3}
            width="46"
            height="16"
            rx="8"
            fill={row.top ? Yellow.Brand : Blue.S08}
          />
          <rect
            x="245"
            y={row.y + 8}
            width="28"
            height="6"
            rx="3"
            fill={row.top ? Neutral.B18 : Blue.Brand}
          />
        </g>
      ))}
    </svg>
  );
};

export const AiAgentsArt: React.FC<ArtProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 320 176"
    role="presentation"
    aria-hidden="true"
    focusable="false"
  >
    <rect
      x="22"
      y="14"
      width="276"
      height="148"
      rx="12"
      fill={WHITE}
      stroke={N_LINE}
      strokeWidth="1.5"
    />

    <rect x="38" y="30" width="30" height="30" rx="8" fill={Blue.Brand} />
    <path
      d="M47 49h12M50 43h6M50 54h6"
      stroke={WHITE}
      strokeWidth="2.4"
      strokeLinecap="round"
    />
    <circle cx="46" cy="43" r="2" fill={Yellow.Brand} />
    <circle cx="60" cy="43" r="2" fill={Yellow.Brand} />
    <circle cx="46" cy="54" r="2" fill={Yellow.Brand} />
    <circle cx="60" cy="54" r="2" fill={Yellow.Brand} />

    <rect x="78" y="34" width="122" height="9" rx="4.5" fill={N_MUTED} />
    <rect x="78" y="49" width="86" height="7" rx="3.5" fill={N_LINE} />

    <path
      d="M81 105h26m53 0h26m53 0h18M94 105v-14h145v14"
      fill="none"
      stroke={Blue.S54}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    {[48, 113, 178].map((x, index) => (
      <g key={x}>
        <rect
          x={x}
          y="78"
          width="48"
          height="54"
          rx="10"
          fill={index === 1 ? Yellow.S45 : Blue.S08}
          stroke={index === 1 ? Yellow.Brand : Blue.S54}
          strokeWidth="1.5"
        />
        <circle
          cx={x + 24}
          cy="96"
          r="7"
          fill={index === 1 ? Yellow.S87 : Blue.Brand}
        />
        <path
          d={`M${x + 14} 115c2-7 18-7 20 0`}
          fill="none"
          stroke={index === 1 ? Yellow.S87 : Blue.Brand}
          strokeWidth="4"
          strokeLinecap="round"
        />
      </g>
    ))}

    <rect x="243" y="91" width="40" height="28" rx="9" fill={Blue.Brand} />
    <path
      d="M253 105h20m-5-5 5 5-5 5"
      fill="none"
      stroke={WHITE}
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <path
      d="M276 28l2.4 6.1 6.1 2.4-6.1 2.4-2.4 6.1-2.4-6.1-6.1-2.4 6.1-2.4z"
      fill={Yellow.Brand}
    />
    <rect x="92" y="143" width="136" height="6" rx="3" fill={N_LINE} />
  </svg>
);
